import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/skeleton";
import type { Product } from "@/lib/schemas/paycode/product";
import { cn } from "@/lib/utils";

type OrderTableProps = {
  products: Product[];
  total: number;
  receipt?: boolean;
  limit?: number;
  onShowAll?: () => void;
  staggerFrom?: number;
};

type ProductRowProps = {
  product: Product;
  delay?: number;
};

export const OrderTable = ({
  products,
  total,
  receipt,
  limit,
  onShowAll,
  staggerFrom,
}: OrderTableProps) => {
  const t = useTranslations("products");
  const overflow = limit != null && products.length > limit;
  const shown = overflow ? products.slice(0, limit - 1) : products;
  const rest = overflow ? products.slice(limit - 1) : [];

  return (
    <table
      aria-label="Order summary"
      className="w-full border-separate border-spacing-y-3"
    >
      <thead>
        <tr className="text-muted text-xs lg:text-muted-alt">
          <th scope="col" className="text-start font-semibold">
            {t("product")}
          </th>
          <th scope="col" className="text-end font-semibold">
            {t("amount")}
          </th>
        </tr>
      </thead>
      <tbody>
        {shown.map((product, i) => (
          <ProductRow
            key={product.id}
            product={product}
            delay={
              staggerFrom != null && i >= staggerFrom
                ? (i - staggerFrom) * 0.025
                : undefined
            }
          />
        ))}
        {overflow && (
          <>
            <tr className="font-normal">
              <th scope="row" className="w-full max-w-0 text-start font-normal">
                <div className="flex items-baseline gap-1">
                  <span className="inline-block min-w-[2.5ch] shrink-0 text-end tabular-nums">
                    {rest.reduce((sum, p) => sum + p.quantity, 0)}
                    <span className="text-xs">×</span>
                  </span>
                  <span className="min-w-0 truncate">{t("others")}</span>
                </div>
              </th>
              <td className="whitespace-nowrap pl-4 text-end">
                {rest
                  .reduce((sum, p) => sum + p.quantity * p.amount, 0)
                  .toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
              </td>
            </tr>
            {onShowAll && (
              <tr>
                <td colSpan={2} className="text-center">
                  <button
                    type="button"
                    onClick={onShowAll}
                    className="text-accent text-sm opacity-100 hover:underline hover:opacity-80"
                  >
                    {t("showAll")}
                  </button>
                </td>
              </tr>
            )}
          </>
        )}
      </tbody>
      <tfoot>
        <tr
          className={cn(
            "[&>*]:border-border [&>*]:border-t [&>*]:border-dashed [&>*]:pt-3 lg:[&>*]:border-border-alt",
            receipt && "[&>*]:border-border/20!",
          )}
        >
          <th scope="row" className="text-start font-normal">
            {t("total")}
          </th>
          <td className="text-end font-normal">
            {total.toLocaleString("es-MX", {
              style: "currency",
              currency: "MXN",
            })}
            <span className="ml-1 font-medium text-muted text-xs lg:text-muted-alt">
              MXN
            </span>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

export const OrderTableSkeleton = ({
  products = 1,
  limit,
}: {
  products?: number;
  limit?: number;
}) => {
  const overflow = limit != null && products > limit;
  const rows = overflow ? limit - 1 : products;

  const skeletonRow = (key: string) => (
    <tr key={key}>
      <th scope="row" className="w-full max-w-0 text-start">
        <div className="flex items-baseline gap-1">
          <Skeleton className="h-6 w-9" />
          <Skeleton className="h-6 w-40" />
        </div>
      </th>
      <td className="pl-4">
        <div className="flex items-center justify-end">
          <Skeleton className="h-6 w-20" />
        </div>
      </td>
    </tr>
  );

  return (
    <table
      aria-label="Order summary"
      className="w-full border-separate border-spacing-y-3"
    >
      <thead>
        <tr>
          <th scope="col" className="text-start">
            <Skeleton className="h-4 w-16 rounded-lg" />
          </th>
          <th scope="col">
            <div className="flex justify-end">
              <Skeleton className="h-4 w-12 rounded-lg" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, i) => skeletonRow(`sk-row-${i}`))}
        {overflow && (
          <>
            {skeletonRow("sk-row-others")}
            <tr>
              <td colSpan={2}>
                <div className="flex justify-center">
                  <Skeleton className="h-5 w-24" />
                </div>
              </td>
            </tr>
          </>
        )}
      </tbody>
      <tfoot>
        <tr className="[&>*]:border-border [&>*]:border-t [&>*]:border-dashed [&>*]:pt-3 lg:[&>*]:border-border-alt">
          <th scope="row" className="text-start">
            <Skeleton className="h-6 w-16" />
          </th>
          <td>
            <div className="flex justify-end">
              <Skeleton className="h-6 w-20" />
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

const ProductRow = ({ product, delay }: ProductRowProps) => {
  return (
    <motion.tr
      className="font-normal"
      initial={delay == null ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay }}
    >
      <th scope="row" className="w-full max-w-0 text-start font-normal">
        <div className="flex items-baseline gap-1">
          <span className="inline-block min-w-[2.5ch] shrink-0 text-end tabular-nums">
            {product.quantity}
            <span className="text-xs">×</span>
          </span>
          <span className="min-w-0 truncate">{product.name}</span>
        </div>
      </th>
      <td className="whitespace-nowrap pl-4 text-end">
        {(product.quantity * product.amount).toLocaleString("es-MX", {
          style: "currency",
          currency: "MXN",
        })}
        <span className="ml-1 font-medium text-muted text-xs lg:text-muted-alt">
          MXN
        </span>
      </td>
    </motion.tr>
  );
};
